import { useCallback, useMemo } from "react"
import { useAccountAlias } from "resources/account"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import {
  type DirectoryEntry,
  type ShareRecipient,
  artifactShareUrl,
  useArtifactShare,
  useCreateArtifactShare,
} from "./resource-sharing"
import { ShareDialog, type ShareDialogWording, recipientAnalyticsFields } from "./share-dialog"

interface ArtifactShareDialogProps {
  open: boolean
  onClose: () => void
  artifactId: string
  artifactName: string
  threadId: string
}

const ARTIFACT_SHARE_WORDING: ShareDialogWording = {
  title: "Share artifact",
  accessNote: "Everyone you add can view this artifact. They can't edit or re-share it.",
  shareFailure: "Couldn't share this artifact. Try again.",
}

export function ArtifactShareDialog({ open, onClose, artifactId, artifactName, threadId }: ArtifactShareDialogProps) {
  const accountAlias = useAccountAlias()
  const analytics = useAnalytics()

  const { share, isLoading: isLoadingShare, isError: isShareErrored } = useArtifactShare(artifactId, open)
  const createShare = useCreateArtifactShare()

  // The resource has to exist before a grant can name it, and it is created lazily: opening the
  // dialog must not promote an artifact the user then decides not to share.
  const resolveResourceId = useCallback(async () => {
    if (share != null) return share.resourceId
    const created = await createShare.mutateAsync({ threadId, artifactId })
    return created.resourceId
  }, [share, createShare, threadId, artifactId])

  const onShared = useCallback((resourceId: string, granted: DirectoryEntry[]) => {
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_SHARED, {
      artifactId,
      resourceId,
      ...recipientAnalyticsFields(granted),
    })
  }, [analytics, artifactId])

  const onUnshared = useCallback((resourceId: string, recipient: ShareRecipient) => {
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_UNSHARED, {
      artifactId,
      resourceId,
      recipientKind: recipient.kind,
      recipientId: recipient.id,
    })
  }, [analytics, artifactId])

  const onLinkCopied = useCallback((resourceId: string) => {
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_SHARE_LINK_COPIED, { artifactId, resourceId })
  }, [analytics, artifactId])

  const copyLink = useMemo(() => ({
    url: (resourceId: string) => artifactShareUrl(accountAlias, resourceId),
    failure: "Couldn't create a link for this artifact. Try again.",
    onCopied: onLinkCopied,
  }), [accountAlias, onLinkCopied])

  const target = {
    resourceId: share?.resourceId ?? null,
    isLoading: isLoadingShare,
    isError: isShareErrored,
    isCreating: createShare.isPending,
    resolveResourceId,
  }

  return (
    <ShareDialog
      open={open}
      onClose={onClose}
      resourceType="ARTIFACT"
      resourceName={artifactName}
      target={target}
      wording={ARTIFACT_SHARE_WORDING}
      idPrefix="artifact-share"
      copyLink={copyLink}
      onShared={onShared}
      onUnshared={onUnshared}
    />
  )
}
